import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearch } from '../contexts/SearchContext';
import { Home, Clock, Star, Shield, LogOut, ChevronRight } from 'react-feather';
import { FiSearch } from 'react-icons/fi';
import { isAdmin } from '../services/userManagement';
import { getUserDocument } from '../services/userService';
import Logo from './Logo';
import './Layout.css';

const Layout = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const { searchQuery, setSearchQuery } = useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [userPlan, setUserPlan] = useState('FREE');
  const [userDisplayName, setUserDisplayName] = useState('');
  const contentWrapperRef = useRef(null);

  // Fix inline styles that cause sidebar overlap - CRITICAL FIX
  useEffect(() => {
    const fixOverlapStyles = () => {
      if (!contentWrapperRef.current) return;
      
      // Find all direct children and fix problematic inline styles
      const children = contentWrapperRef.current.children;
      Array.from(children).forEach((child) => {
        if (child && child.style) {
          // CRITICAL: Force width to 100% if not set or if using viewport units
          if (!child.style.width || 
              child.style.width.includes('100vw') || 
              child.style.width.includes('vw') ||
              child.style.width === 'auto') {
            child.style.width = '100%';
          }
          
          // CRITICAL: Always enforce max-width
          child.style.maxWidth = '100%';
          
          // CRITICAL: Fix box-sizing
          child.style.boxSizing = 'border-box';
          
          // CRITICAL: Ensure no negative margins that could cause overflow
          if (child.style.marginLeft && parseFloat(child.style.marginLeft) < 0) {
            child.style.marginLeft = '0';
          }
          if (child.style.marginRight && parseFloat(child.style.marginRight) < 0) {
            child.style.marginRight = '0';
          }
          
          // CRITICAL: Prevent fixed/absolute positioning from escaping
          if (child.style.position === 'fixed' || child.style.position === 'absolute') {
            // Only allow if it's within the container bounds
            if (child.style.left === '0' || child.style.left === '0px') {
              child.style.left = 'auto';
            }
          }
        }
      });
    };

    // Run immediately using requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      fixOverlapStyles();
    });
    
    // Use MutationObserver to catch dynamically rendered content
    const observer = new MutationObserver(() => {
      // Use requestAnimationFrame to ensure DOM is updated before fixing
      requestAnimationFrame(() => {
        fixOverlapStyles();
      });
    });
    
    if (contentWrapperRef.current) {
      observer.observe(contentWrapperRef.current, {
        childList: true,
        subtree: true, // Watch all descendants for style changes
        attributes: true,
        attributeFilter: ['style']
      });
    }
    
    // Also run after delays to catch any late-rendered content
    // React may batch updates, so we need multiple attempts
    const timeoutId = setTimeout(() => requestAnimationFrame(fixOverlapStyles), 100);
    const timeoutId2 = setTimeout(() => requestAnimationFrame(fixOverlapStyles), 300);
    const timeoutId3 = setTimeout(() => requestAnimationFrame(fixOverlapStyles), 500);
    const timeoutId4 = setTimeout(() => requestAnimationFrame(fixOverlapStyles), 1000);
    
    // Run on resize
    window.addEventListener('resize', fixOverlapStyles);
    
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(timeoutId4);
      window.removeEventListener('resize', fixOverlapStyles);
    };
  }, [location.pathname]); // Re-run when route changes

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser?.uid) {
        try {
          const adminStatus = await isAdmin(currentUser.uid);
          setIsUserAdmin(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsUserAdmin(false);
        }

        // Get user document for plan and display name
        try {
          const userDoc = await getUserDocument(currentUser.uid);
          if (userDoc) {
            setUserPlan(userDoc.plan?.toUpperCase() || 'FREE');
            setUserDisplayName(userDoc.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'User');
          } else {
            setUserDisplayName(currentUser.displayName || currentUser.email?.split('@')[0] || 'User');
          }
        } catch (error) {
          console.error('Error fetching user document:', error);
          setUserDisplayName(currentUser.displayName || currentUser.email?.split('@')[0] || 'User');
        }
      }
    };
    checkAdminStatus();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Failed to logout', err);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Navigate to history page when searching
    if (e.target.value && location.pathname !== '/videos') {
      navigate('/videos');
    }
  };

  // Get user initial for avatar
  const getUserInitial = () => {
    if (userDisplayName) {
      return userDisplayName.charAt(0).toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const navLinks = [
    { path: '/videos', icon: Home, label: 'Videos', gridIcon: true },
    { path: '/recent', icon: Clock, label: 'Recent' },
    { path: '/favourites', icon: Star, label: 'Favourites' },
  ];

  if (isUserAdmin) {
    navLinks.push({ path: '/admin/users', icon: Shield, label: 'Users' });
  }

  return (
    <div className="min-h-screen flex flex-row font-sans bg-background-dark">
      {/* Sidebar */}
      <aside className="sidebar-responsive bg-background-dark p-[30px] flex flex-col border-r border-white border-opacity-5 fixed top-0 left-0 max-h-screen h-screen overflow-y-auto overflow-x-hidden z-[100]">
        {/* Logo */}
        <Link
          to="/"
          className="logo-container block mb-8 no-underline"
        >
          <Logo />
        </Link>

        {/* Search */}
        <div className="mb-6 relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-white text-base pointer-events-none z-[1]" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search videos"
            className="w-full py-2.5 pl-9 pr-3 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-md text-white text-sm outline-none transition-all duration-300 font-sans font-normal box-border focus:bg-white focus:bg-opacity-10 focus:border-secondary-500 focus:border-opacity-50"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-shrink-0">
          {navLinks.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 p-3 rounded-md no-underline text-sm font-medium mb-1 transition-all duration-200 ${
                  isActive 
                    ? 'text-white bg-primary-500' 
                    : 'text-white text-opacity-80 bg-transparent hover:bg-secondary-500 hover:bg-opacity-15 hover:text-white'
                }`}
              >
                {link.gridIcon ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                ) : (
                  <Icon size={20} style={{ opacity: 0.9 }} />
                )}
                <span className="nav-label">{link.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Profile */}
        <div
          className="user-profile flex items-center gap-3 p-3 rounded-md bg-white bg-opacity-5 mt-6 cursor-pointer transition-colors duration-200 flex-shrink-0 hover:bg-white hover:bg-opacity-10"
          onClick={() => navigate('/profile')}
        >
          <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center font-semibold text-base text-white flex-shrink-0">
            {getUserInitial()}
          </div>
          <div className="user-info flex-1 min-w-0">
            <div className="text-sm font-semibold mb-0.5 text-white">
              {userDisplayName}
            </div>
            <span className="inline-block px-2 py-0.5 bg-white bg-opacity-10 rounded text-[11px] font-semibold text-white">
              {userPlan}
            </span>
          </div>
          <ChevronRight size={16} className="opacity-50 text-white flex-shrink-0" />
        </div>

        {/* Logout */}
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            handleLogout();
          }}
          className="flex items-center gap-3 p-3 rounded-md text-white text-opacity-80 no-underline text-sm font-medium mt-2 transition-all duration-200 flex-shrink-0 hover:bg-white hover:bg-opacity-10 hover:text-white"
        >
          <LogOut size={20} className="opacity-90" />
          <span className="logout-text">Logout</span>
        </Link>
      </aside>

      {/* Main Content */}
      <main className="main-content-responsive flex-1 min-w-0">
        <div ref={contentWrapperRef} className="main-content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
